
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { AnimatePresence, motion } from "framer-motion";

import {
  AlertCircle,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  Loader2,
  RefreshCw,
  Sparkles,
  X,
  ZoomIn,
} from "lucide-react";

import { api, resolveMediaUrl } from "../lib/api";

/* ==========================================
   HELPERS
========================================== */

function formatDate(value) {
  if (!value) return "";

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
}

function getRecords(response) {
  if (Array.isArray(response)) return response;

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.data?.data)) {
    return response.data.data;
  }

  return [];
}

/* ==========================================
   GALLERY CARD
========================================== */

function GalleryCard({ image, index, onOpen }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{
        duration: 0.35,
        delay: Math.min(index * 0.035, 0.2),
      }}
      className="group min-w-0"
    >
      <button
        type="button"
        onClick={() => onOpen(image)}
        aria-label={`View ${image.title}`}
        className="flex h-full w-full flex-col overflow-hidden rounded-[20px] border border-red-100 bg-white text-left shadow-[0_10px_30px_rgba(80,25,25,0.06)] transition-all duration-300 hover:-translate-y-1 hover:border-red-200 hover:shadow-[0_18px_40px_rgba(80,25,25,0.12)]"
      >
        {/* CLEAN IMAGE */}
        <div className="relative h-[220px] w-full overflow-hidden bg-slate-100 sm:h-[230px]">

          {!imageLoaded && !imageFailed && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2
                size={24}
                className="animate-spin text-red-700"
              />
            </div>
          )}

          {imageFailed ? (
            <div className="flex h-full items-center justify-center bg-red-50 text-red-300">
              <ImageIcon size={42} strokeWidth={1.4} />
            </div>
          ) : (
            <img
              src={image.src}
              alt={image.title}
              loading="lazy"
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageFailed(true)}
              className={`h-full w-full object-cover transition-all duration-700 ease-out group-hover:scale-[1.045] ${
                imageLoaded ? "opacity-100" : "opacity-0"
              }`}
            />
          )}

          {/* IMAGE CORNER DETAIL */}
          <span className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full border border-white/80 bg-white/95 text-red-800 shadow-sm transition-transform duration-300 group-hover:scale-110">
            <ZoomIn size={17} />
          </span>
        </div>

        {/* CARD ACCENT */}
        <div className="h-[3px] w-full bg-gradient-to-r from-red-800 via-red-500 to-amber-400" />

        {/* CARD CONTENT */}
        <div className="flex w-full flex-1 flex-col px-4 pb-4 pt-4">
          <span className="w-fit rounded-full border border-red-100 bg-red-50 px-2.5 py-1 text-[10px] font-normal text-red-800">
            {image.category}
          </span>

          <h3 className="mt-3 line-clamp-2 font-serif text-[17px] font-medium leading-snug text-slate-900 transition-colors group-hover:text-red-800">
            {image.title}
          </h3>

          {image.date && (
            <p className="mt-auto flex items-center gap-1.5 pt-3 text-[11px] font-normal text-slate-500">
              <CalendarDays
                size={14}
                className="text-amber-600"
              />
              {image.date}
            </p>
          )}
        </div>
      </button>
    </motion.article>
  );
}

/* ==========================================
   MAIN GALLERY
========================================== */

export default function Gallery() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [activeCategory, setActiveCategory] =
    useState("All");

  const [currentId, setCurrentId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  /* ==========================================
     LOAD GALLERY IMAGES
  ========================================== */

  useEffect(() => {
    let active = true;

    async function loadGallery() {
      setLoading(true);
      setError("");

      try {
        const response = await api.activeGalleries();

        if (!active) return;

        const records = getRecords(response);

        const galleryImages = records
          .filter((item) => item.isActive !== false)
          .map((item, index) => {
            const originalUrl =
              item.image?.url ||
              item.image?.path ||
              (typeof item.image === "string"
                ? item.image
                : "");

            return {
              id: item._id || String(index),
              src: originalUrl
                ? resolveMediaUrl(originalUrl)
                : "",
              title:
                item.title ||
                item.image?.alt ||
                "School Gallery",
              category: item.category || "Campus",
              date: formatDate(item.date),
            };
          })
          .filter((item) => Boolean(item.src));

        setImages(galleryImages);
      } catch (requestError) {
        if (!active) return;

        setError(
          requestError?.message ||
            "Unable to load school gallery."
        );
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadGallery();

    return () => {
      active = false;
    };
  }, [refreshKey]);

  /* ==========================================
     CATEGORIES AND FILTERING
  ========================================== */

  const categories = useMemo(() => {
    return [
      "All",
      ...new Set(
        images
          .map((image) => image.category)
          .filter(Boolean)
      ),
    ];
  }, [images]);

  const filteredImages = useMemo(() => {
    return activeCategory === "All"
      ? images
      : images.filter(
          (image) => image.category === activeCategory
        );
  }, [activeCategory, images]);

  const selectedIndex = useMemo(() => {
    return filteredImages.findIndex(
      (image) => image.id === currentId
    );
  }, [filteredImages, currentId]);

  const selectedImage =
    selectedIndex >= 0
      ? filteredImages[selectedIndex]
      : null;

  /* ==========================================
     LIGHTBOX HANDLERS
  ========================================== */

  function openModal(image) {
    setCurrentId(image.id);
  }

  const closeModal = useCallback(() => {
    setCurrentId(null);
  }, []);

  const changeImage = useCallback(
    (direction) => {
      if (!filteredImages.length) return;

      setCurrentId((previousId) => {
        const currentIndex = filteredImages.findIndex(
          (image) => image.id === previousId
        );

        const nextIndex =
          (currentIndex + direction + filteredImages.length) %
          filteredImages.length;

        return filteredImages[nextIndex].id;
      });
    },
    [filteredImages]
  );

  /* ==========================================
     KEYBOARD NAVIGATION
  ========================================== */

  useEffect(() => {
    if (!selectedImage) return;

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        closeModal();
      }

      if (event.key === "ArrowLeft") {
        changeImage(-1);
      }

      if (event.key === "ArrowRight") {
        changeImage(1);
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [selectedImage, changeImage, closeModal]);

  /* ==========================================
     BODY SCROLL LOCK
  ========================================== */

  useEffect(() => {
    if (!selectedImage) return;

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedImage]);

  /* ==========================================
     MAIN PAGE
  ========================================== */

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#FFFCF9] pb-16">

      {/* BACKGROUND DECORATION */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-32 top-[350px] h-80 w-80 rounded-full bg-red-100/50 blur-3xl" />

        <div className="absolute -right-32 bottom-0 h-96 w-96 rounded-full bg-amber-100/60 blur-3xl" />

        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage:
              "radial-gradient(#991b1b 1px, transparent 1px)",
            backgroundSize: "27px 27px",
          }}
        />
      </div>

      {/* PAGE HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-red-900 via-red-800 to-[#b42323] px-4 pb-24 pt-12 text-white sm:px-6 lg:px-8">

        <div className="pointer-events-none absolute -right-24 -top-28 h-80 w-80 rounded-full border-[44px] border-white/5" />

        <div className="pointer-events-none absolute bottom-0 left-1/3 h-40 w-40 rounded-full bg-amber-300/10 blur-3xl" />

        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative mx-auto max-w-7xl text-center"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-white/10 px-4 py-2 text-[11px] font-normal tracking-wide text-amber-100">
            <Sparkles size={15} />
            Life at St. Xavier&apos;s
          </span>

          <h1 className="mt-5 font-serif text-[33px] font-medium leading-tight sm:text-[43px] lg:text-[51px]">
            Photo{" "}
            <span className="text-amber-300">
              Gallery
            </span>
          </h1>

          <div className="mx-auto mt-4 h-1 w-20 rounded-full bg-amber-400" />

          <p className="mx-auto mt-5 max-w-2xl text-sm font-normal leading-7 text-white/85 sm:text-[15px]">
            A collection of memorable moments, learning
            experiences, celebrations and activities
            from St. Xavier&apos;s Public School.
          </p>
        </motion.div>
      </section>

      {/* GALLERY CONTENT */}
      <div className="relative z-10 mx-auto -mt-11 max-w-7xl px-4 sm:px-6 lg:px-8">

        {/* FILTER PANEL */}
        <div className="mb-7 rounded-[20px] border border-red-100 bg-white px-4 py-5 shadow-[0_12px_35px_rgba(80,25,25,0.07)] sm:px-6">

          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">

            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-red-800">
                <ImageIcon size={20} />
              </div>

              <div>
                <h2 className="font-serif text-lg font-medium text-slate-900">
                  Explore Our Gallery
                </h2>

                <p className="mt-1 text-xs text-slate-500">
                  Browse photos by category
                </p>
              </div>
            </div>

            {!loading && !error && (
              <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
                {filteredImages.length}{" "}
                {filteredImages.length === 1
                  ? "Photo"
                  : "Photos"}
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {categories.map((category) => {
              const isActive =
                activeCategory === category;

              return (
                <button
                  key={category}
                  type="button"
                  onClick={() => {
                    setActiveCategory(category);
                    closeModal();
                  }}
                  className={`rounded-full border px-4 py-2 text-[12px] font-medium transition-all duration-200 ${
                    isActive
                      ? "border-red-800 bg-red-800 text-white shadow-sm"
                      : "border-slate-200 bg-white text-slate-600 hover:border-red-200 hover:bg-red-50 hover:text-red-800"
                  }`}
                >
                  {category}
                </button>
              );
            })}
          </div>
        </div>

        {/* LOADING STATE */}
        {loading ? (
          <div className="flex min-h-[320px] flex-col items-center justify-center rounded-2xl border border-red-100 bg-white shadow-sm">
            <Loader2
              size={32}
              className="animate-spin text-red-800"
            />

            <p className="mt-4 text-sm text-slate-500">
              Loading school gallery...
            </p>
          </div>
        ) : error ? (
          /* ERROR STATE */
          <div className="mx-auto max-w-xl rounded-2xl border border-red-200 bg-white px-6 py-10 text-center shadow-sm">

            <AlertCircle
              size={34}
              className="mx-auto text-red-700"
            />

            <h2 className="mt-3 font-serif text-xl font-medium text-slate-900">
              Unable to Load Gallery
            </h2>

            <p className="mt-2 text-sm leading-6 text-red-700">
              {error}
            </p>

            <button
              type="button"
              onClick={() =>
                setRefreshKey((value) => value + 1)
              }
              className="mt-5 inline-flex items-center gap-2 rounded-xl bg-red-800 px-5 py-2.5 text-sm text-white transition hover:bg-red-900"
            >
              <RefreshCw size={16} />
              Try Again
            </button>
          </div>
        ) : filteredImages.length === 0 ? (
          /* EMPTY STATE */
          <div className="rounded-2xl border border-red-100 bg-white px-6 py-16 text-center shadow-sm">

            <ImageIcon
              size={44}
              className="mx-auto text-red-200"
            />

            <h2 className="mt-4 font-serif text-xl font-medium text-slate-900">
              No Photos Available
            </h2>

            <p className="mt-2 text-sm text-slate-500">
              No images were found in this category.
            </p>
          </div>
        ) : (
          /* PHOTO GRID */
          <motion.div
            layout
            className="grid grid-cols-1 items-stretch gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
          >
            <AnimatePresence mode="popLayout">
              {filteredImages.map((image, index) => (
                <GalleryCard
                  key={image.id}
                  image={image}
                  index={index}
                  onOpen={openModal}
                />
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>

      {/* ======================================
          FULLSCREEN IMAGE VIEWER
      ====================================== */}

      <AnimatePresence>
        {selectedImage && (
          <motion.div
            key="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            role="dialog"
            aria-modal="true"
            aria-label="Gallery image viewer"
            className="fixed inset-0 z-[100] flex items-center justify-center bg-[#100909]/95 p-3 backdrop-blur-md sm:p-6"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                closeModal();
              }
            }}
          >
            {/* CLOSE BUTTON */}
            <button
              type="button"
              onClick={closeModal}
              aria-label="Close gallery viewer"
              className="absolute right-4 top-4 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white transition hover:bg-white/20 sm:right-7 sm:top-6"
            >
              <X size={21} />
            </button>

            {/* PREVIOUS BUTTON */}
            {filteredImages.length > 1 && (
              <button
                type="button"
                onClick={() => changeImage(-1)}
                aria-label="Previous image"
                className="absolute left-2 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white transition hover:bg-white/20 sm:left-7 sm:h-12 sm:w-12"
              >
                <ChevronLeft size={27} />
              </button>
            )}

            {/* NEXT BUTTON */}
            {filteredImages.length > 1 && (
              <button
                type="button"
                onClick={() => changeImage(1)}
                aria-label="Next image"
                className="absolute right-2 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white transition hover:bg-white/20 sm:right-7 sm:h-12 sm:w-12"
              >
                <ChevronRight size={27} />
              </button>
            )}

            {/* IMAGE VIEWER */}
            <div
              className="flex max-h-[90vh] w-full max-w-6xl flex-col items-center justify-center px-6 sm:px-14"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <motion.img
                key={selectedImage.id}
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.25 }}
                src={selectedImage.src}
                alt={selectedImage.title}
                className="max-h-[72vh] max-w-full rounded-xl object-contain shadow-2xl"
              />

              {/* IMAGE DETAILS */}
              <div className="mt-5 text-center">
                <h3 className="font-serif text-lg font-medium text-white sm:text-xl">
                  {selectedImage.title}
                </h3>

                <div className="mt-3 flex flex-wrap items-center justify-center gap-3">
                  <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs text-white/85">
                    {selectedImage.category}
                  </span>

                  {selectedImage.date && (
                    <span className="flex items-center gap-1.5 text-xs text-white/65">
                      <CalendarDays size={13} />
                      {selectedImage.date}
                    </span>
                  )}
                </div>

                <p className="mt-3 text-xs text-white/50">
                  {selectedIndex + 1} / {filteredImages.length}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
